// @flow
import React from 'react';
import classnames from 'classnames';
import RenderHTML from 'html-react-parser';
import { Button, Icon } from '@elements';
import { fsPrintf, isIOSApp, openBillingPage } from '@helpers/helpers';
import { APP_STORE_SUBSCRIPTIONS_URL } from '../../StoreReview/StoreReviewPlan';
import './PlanSummary.scss';

export { APP_STORE_SUBSCRIPTIONS_URL };

// A billing page outside the app: the system browser in the apps, a new tab on the web
export const openExternal = (url: ?string) => {
  if (!url) return;
  if (window.isCordovaApp) openBillingPage(url);
  else window.open(url, '_blank', 'noopener');
};

type State = 'active' | 'app_store' | 'app_store_inactive' | 'will_cancel' | 'payment_failed' | 'blocked'
  | 'boleto_open' | 'boleto_late' | 'boleto_overdue' | 'canceled' | 'trial_ended' | 'inactive';

// The API's warning (PaymentParams#warning) folded into the few states the page shows. The warning only
// knows card and boleto charges: a late PIX or bolepix has none, only the past_due status.
const subscriptionState = (sbInfo: Object): State => {
  const { warning, subscriptionStatus } = sbInfo;
  // The App Store bills it, and only Apple's page fixes or cancels it
  if (sbInfo.paymentMethod === 'apple') {
    return ['expired', 'canceled'].includes(warning) || ['past_due', 'expired', 'canceled'].includes(subscriptionStatus)
      ? 'app_store_inactive' : 'app_store';
  }
  switch (warning) {
    case 'will_cancel': return 'will_cancel';
    case 'canceled': return 'canceled';
    case 'expired': return 'blocked';
    case 'cc_past_due': return 'payment_failed';
    case 'boleto_active': return 'boleto_open';
    case 'boleto_past_due': return 'boleto_late';
    case 'boleto_active_expired_charge':
    case 'boleto_past_due_expired_charge': return 'boleto_overdue';
    default: return subscriptionStatus === 'past_due' ? 'payment_failed' : 'active';
  }
};

// States that say the account works
const OPEN_STATES = ['active', 'app_store', 'will_cancel'];

// An account the app blocks, by the status its gate saw (`sbInfo.blockedStatus`, from the user)
const blockedState = (sbInfo: Object): State => {
  if (sbInfo.paymentMethod === 'apple') return 'app_store_inactive';
  switch (sbInfo.blockedStatus) {
    case 'expired': return 'blocked';
    case 'canceled': return 'canceled';
    case 'end_of_trial': return 'trial_ended';
    default: return 'inactive'; // banned: paying doesn't lift it
  }
};

// The page never contradicts the app: an account the app blocks (`sbInfo.blocked`, from the user) is not
// "Ativa", whatever the plan's request said. An App Store plan read "Ativa" for any status but past_due,
// expired and canceled (banned, end_of_trial too), and the block and the plan come from two requests.
export const planState = (sbInfo: Object): State => {
  const state = subscriptionState(sbInfo);
  return sbInfo.blocked && OPEN_STATES.includes(state) ? blockedState(sbInfo) : state;
};

const TONES = {
  active: 'ok',
  app_store: 'ok',
  app_store_inactive: 'bad',
  will_cancel: 'warn',
  payment_failed: 'bad',
  blocked: 'bad',
  boleto_open: 'warn',
  boleto_late: 'bad',
  boleto_overdue: 'bad',
  canceled: 'plain',
  trial_ended: 'plain',
  inactive: 'bad',
};

// The web price list doesn't apply to what Apple charges, nor to a plan that ended
const NO_PRICE = ['canceled', 'app_store', 'app_store_inactive', 'trial_ended', 'inactive'];

type Props = {
  sbInfo: Object,
  onManageBilling: Function,
  onUpdateCard: Function,
  onGetNewBoleto: Function,
  onResubscribeClick: Function,
  isRequestingNewBoleto: boolean,
  openingBillingPage: ?string,
  _: Function,
};

// Meu Plano: the plan, its state in one line, and the one thing to do about it. Paying, the invoices and
// changing the plan happen on the web billing pages (openWebBilling), not in the app.
const PlanSummary = ({
  sbInfo, onManageBilling, onUpdateCard, onGetNewBoleto, onResubscribeClick, isRequestingNewBoleto, openingBillingPage, _,
}: Props) => {
  const state = planState(sbInfo);
  const opening = !!openingBillingPage;

  const manageButton = (primary: boolean) => (
    <Button
      key="manage"
      text={_('action/manage_plan_and_payments')}
      className={primary ? 'primary' : ''}
      onClick={onManageBilling}
      isLoading={openingBillingPage === 'billing'}
      disabled={opening}
      loadingMessage={_('label/opening_billing')}
    />
  );

  const dueDate = sbInfo.nextPaymentDate.trim();
  // A card is fixed on the card page, anything else at the invoice's checkout, when there is one
  const payActions = () => {
    if (sbInfo.paymentMethod === 'credit_card') {
      return [
        <Button
          key="card"
          className="primary"
          text={_('action/updated_credit_card')}
          onClick={onUpdateCard}
          isLoading={openingBillingPage === 'new_card'}
          disabled={opening}
          loadingMessage={_('label/opening_billing')}
        />,
        manageButton(false),
      ];
    }
    if (sbInfo.invoicePaymentLink) {
      return [<Button key="pay" className="primary" text={_('summary/pay_now')} onClick={() => openExternal(sbInfo.invoicePaymentLink)} />, manageButton(false)];
    }
    return [manageButton(true)];
  };
  const supportButton = <Button key="support" className="primary" text={_('summary/help_link')} intercom />;
  const appStoreActions = () => {
    if (isIOSApp()) return [<Button key="apple" className="primary" text={_('store_review/app_store_manage')} onClick={() => openExternal(APP_STORE_SUBSCRIPTIONS_URL)} />];
    // Blocked, on the web or Android: moving off the App Store goes through the support (AppStoreSwitch)
    return sbInfo.blocked ? [supportButton] : [];
  };

  let line = '';
  let actions = [manageButton(true)];
  switch (state) {
    case 'active':
      line = dueDate ? _('summary/next_charge', null, fsPrintf(dueDate)) : '';
      break;
    case 'app_store':
      line = _('summary/app_store');
      // On the web and Android, AppStoreSwitch explains the way out
      actions = appStoreActions();
      break;
    case 'app_store_inactive':
      line = _('summary/app_store_inactive');
      actions = appStoreActions();
      break;
    case 'will_cancel':
      line = dueDate ? _('summary/will_cancel', null, fsPrintf(dueDate)) : '';
      break;
    case 'payment_failed':
      line = dueDate ? _('summary/payment_failed', null, fsPrintf(dueDate)) : _('summary/payment_failed_nodate');
      actions = payActions();
      break;
    case 'blocked':
      line = dueDate ? _('summary/blocked', null, fsPrintf(dueDate)) : _('summary/blocked_nodate');
      actions = payActions();
      break;
    case 'boleto_open':
      line = _('summary/boleto_open', null, fsPrintf(sbInfo.boleto.price, sbInfo.boleto.dueDate));
      actions = [<Button key="boleto" className="primary" text={_('action/view_boleto')} onClick={() => openExternal(sbInfo.boleto.url)} />, manageButton(false)];
      break;
    case 'boleto_late':
      line = _('summary/boleto_late', null, fsPrintf(dueDate, sbInfo.boleto.price, sbInfo.boleto.dueDate));
      actions = [<Button key="boleto" className="primary" text={_('action/view_boleto')} onClick={() => openExternal(sbInfo.boleto.url)} />, manageButton(false)];
      break;
    case 'boleto_overdue':
      line = _('summary/boleto_overdue', null, fsPrintf(sbInfo.boleto.dueDate));
      // The invoice's checkout makes a new boleto or PIX on the spot; without it, ask the API for a new boleto
      actions = [
        sbInfo.invoicePaymentLink
          ? <Button key="pay" className="primary" text={_('summary/pay_now')} onClick={() => openExternal(sbInfo.invoicePaymentLink)} />
          : <Button key="boleto" className="primary" text={_('action/request_new_boleto')} isLoading={isRequestingNewBoleto} onClick={onGetNewBoleto} />,
        manageButton(false),
      ];
      break;
    case 'canceled':
      line = _('summary/canceled');
      actions = [<Button key="again" className="primary" text={_('action/subscribe_again')} onClick={onResubscribeClick} />];
      break;
    case 'trial_ended':
      line = _('summary/trial_ended');
      actions = [<Button key="plans" className="primary" text={_('action/see_plans')} onClick={onResubscribeClick} />];
      break;
    case 'inactive':
      line = _('summary/inactive');
      actions = [supportButton];
      break;
    default:
      break;
  }

  return (
    <section className="planSummary">
      <div className="planSummary-top">
        <div className="planSummary-plan">
          <span className="planSummary-label">{_('summary/your_plan')}</span>
          <h1>{sbInfo.planName || _('label/plan')}</h1>
          {Boolean(sbInfo.price) && !NO_PRICE.includes(state) && (
            <span className="planSummary-price">{sbInfo.price}{sbInfo.paymentPeriod === 'year' ? _('summary/per_year') : _('summary/per_month')}</span>
          )}
        </div>
        <span className={classnames('planSummary-badge', TONES[state])}>{_(`summary/state/${state}`)}</span>
      </div>
      {line && <p className="planSummary-line">{line}</p>}
      {/* A special contract's terms (doctors, discount, validity), from the admin */}
      {sbInfo.dealSummary && (
        <div className="planSummary-deal">
          <span className="planSummary-label">{_('summary/deal')}</span>
          <p>{RenderHTML(sbInfo.dealSummary)}</p>
        </div>
      )}
      {actions.length > 0 && <div className="planSummary-actions">{actions}</div>}
    </section>
  );
};

export const PlanHelp = ({ _ }: { _: Function }) => (
  <p className="planHelp">
    <Icon iconName="support" size={18} />
    <span>{_('summary/help')}</span>
    <Button className="planHelp-link" text={_('summary/help_link')} intercom />
  </p>
);

export default PlanSummary;
